import React, { useEffect, useRef, useState, useMemo } from 'react';
import { ProjectedShade } from './ProjectedShade';
import { projectQuad } from './shadeProjection';
import { shadeQuad, sceneImage, NIGHT_CITY_SKYLINE, resolveScene } from './roomScenes';
import { OPENNESS_OPACITY } from './fabricLight';
import { useGlide } from './useGlide';

/* When containerAspect overrides the scene's native aspect, the room image is shown via
   object-fit:cover — cropping the wider dimension. This function maps shade quad coordinates
   from image-space (% of original image) to container-space (% of the visible cropped area). */
export const coverTransformQuad = (quad, sceneAspect, containerAspect) => {
  if (!containerAspect || Math.abs(containerAspect - sceneAspect) < 0.001) return quad;
  if (containerAspect > sceneAspect) {
    // Container taller (more portrait) than image → width cropped, height fits exactly
    const visW = sceneAspect / containerAspect;       // visible fraction of image width
    const offX = ((1 - visW) / 2) * 100;              // left crop in image-% coords
    return quad.map(([x, y]) => [(x - offX) / visW, y]);
  }
  // Container wider (more landscape) than image → height cropped, width fits exactly
  const visH = containerAspect / sceneAspect;
  const offY = ((1 - visH) / 2) * 100;
  return quad.map(([x, y]) => [x, (y - offY) / visH]);
};

/* Room photo + fabric shades. light = classifyFabric() result; drop = 0..1 (fully raised → fully lowered), animated like a motor.
   Supports textureUrl (preferred) or hexColor (solid-color fallback) for shade rendering.
   containerAspect (optional, h/w ratio): forces the stage to a fixed aspect ratio; the room image
   is displayed object-fit:cover and shade overlays are repositioned to match the crop. */
/* Skyline view rendered inside a window quad — positioned exactly where the glass is,
   behind the fabric shade but in front of the room background. */
const WindowSkyline = ({ quad, cw, ch }) => {
  if (!cw || !ch) return null;
  const { w, h, transform } = projectQuad(quad, cw, ch);
  return (
    <div style={{
      position: 'absolute', left: 0, top: 0, width: w, height: h,
      transform, transformOrigin: '0 0',
      backgroundImage: `url(${NIGHT_CITY_SKYLINE})`,
      backgroundSize: 'cover', backgroundPosition: 'center 40%',
      zIndex: 1, pointerEvents: 'none',
    }} />
  );
};

const preload = (src) => new Promise((resolve) => {
  if (!src) return resolve();
  const im = new Image();
  im.onload = () => (im.decode ? im.decode().catch(() => {}).then(resolve) : resolve());
  im.onerror = () => resolve();
  im.src = src;
});

export const RoomMockup = ({ scene: rawScene, ambience = 'day', textureUrl, hexColor, light, filterOpacity = 0.85, openness = 3, drop = 0.5, animate = true, onLoad, children, className = '', containerAspect, system = 'roller', fold = 'flat', screenOpacity }) => {
  const stageRef = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const targetScene = useMemo(() => resolveScene(rawScene, ambience), [rawScene, ambience]);
  const targetImage = sceneImage(targetScene, ambience);
  const shownDrop = useGlide(drop, 2.2, animate);

  /* Seamless swaps: the next room photo is decoded by a hidden staging <img> and the next fabric texture
     is preloaded; only when BOTH are ready is `shown` replaced in ONE render. A fabric-only change keeps
     the current room on screen until the new texture is ready; a room/ambience change keeps the old
     room + fabric visible until the new photo and texture are ready. */
  const [shown, setShown] = useState(null);
  const [loadedImgs, setLoadedImgs] = useState(() => new Set());
  const [loadedTex, setLoadedTex] = useState(() => new Set());
  const markImg = (url) => setLoadedImgs((prev) => (prev.has(url) ? prev : new Set(prev).add(url)));
  useEffect(() => {
    if (!textureUrl || loadedTex.has(textureUrl)) return undefined;
    let cancelled = false;
    preload(textureUrl).then(() => { if (!cancelled) setLoadedTex((prev) => new Set(prev).add(textureUrl)); });
    return () => { cancelled = true; };
  }, [textureUrl]); // eslint-disable-line react-hooks/exhaustive-deps
  const roomReady = loadedImgs.has(targetImage);
  const texReady = !textureUrl || loadedTex.has(textureUrl);
  useEffect(() => {
    if (!roomReady || !texReady) return;
    setShown({ scene: targetScene, ambience, imageUrl: targetImage, textureUrl });
    onLoad?.();
  }, [roomReady, texReady, targetScene, ambience, targetImage, textureUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const scene = shown?.scene || targetScene;
  const shownAmbience = shown?.ambience || ambience;
  const displayAspect = containerAspect || scene.aspect;

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const update = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [displayAspect]);

  const mode = light?.mode || 'filtering';
  const opacity = mode === 'blackout' ? 1 : mode === 'screen' ? ((screenOpacity || OPENNESS_OPACITY)[openness] ?? 0.9) : filterOpacity;

  /* Pre-compute transformed quads once per scene/drop/containerAspect change.
     Every shade follows the user's drop control (rooms are retouched so the glass is never blocked).
     A fully raised shade still shows the cassette + hem pocket (min 5% drop), like a real roller. */
  const renderDrop = Math.max(shownDrop, 0.05);
  const transformedShades = useMemo(() =>
    scene.shades.map((shade) => {
      const dropped = shadeQuad(shade.quad, renderDrop);
      return containerAspect ? coverTransformQuad(dropped, scene.aspect, containerAspect) : dropped;
    }),
    [scene, renderDrop, containerAspect]
  );
  /* Hem pocket = 2.6% of the FULL window height, expressed relative to the dropped panel so it keeps
     the same on-screen size at every slider position. */
  const hemFrac = 0.026 / renderDrop;
  const railFrac = 0.02 / renderDrop;

  /* Full-window quads for the skyline overlay (100% drop, covers entire glass area) */
  const isNightCity = shownAmbience === 'nightCity' && !scene.variants?.nightCity; // real Night City photo needs no CSS skyline
  const fullWindowQuads = useMemo(() => {
    if (!isNightCity) return [];
    return scene.shades.map(shade => {
      const full = shadeQuad(shade.quad, 1.0);
      return containerAspect ? coverTransformQuad(full, scene.aspect, containerAspect) : full;
    });
  }, [scene, containerAspect, isNightCity]);

  const ready = !!shown;
  return (
    <div ref={stageRef} className={`relative w-full overflow-hidden ${className}`} style={{ paddingBottom: `${displayAspect * 100}%` }} data-testid="room-mockup-stage" data-drop={shownDrop.toFixed(2)} data-moving={Math.abs(shownDrop - drop) > 0.001 ? 'true' : 'false'} data-ready={ready ? 'true' : 'false'}>
      {/* Current room photo + (while switching) the next photo decoding invisibly in its own element —
          revealed in the same render that swaps quads/texture, so the old room never goes blank. */}
      {[...(ready ? [shown.imageUrl] : []), ...(targetImage !== shown?.imageUrl ? [targetImage] : [])].map((url) => {
        const isShown = url === shown?.imageUrl;
        return (
          <img
            key={url}
            src={url}
            alt={isShown ? `${scene.name} — ${shownAmbience}` : ''}
            aria-hidden={isShown ? undefined : 'true'}
            className="absolute inset-0 w-full h-full object-cover"
            style={isShown ? undefined : { opacity: 0, pointerEvents: 'none' }}
            onLoad={isShown ? undefined : () => markImg(url)}
            onError={isShown ? undefined : () => markImg(url)}
            data-testid={isShown ? 'room-preview-image' : 'room-staging-image'}
          />
        );
      })}
      {/* Night City skyline overlay — rendered inside window quads behind the shade */}
      {isNightCity && ready && fullWindowQuads.map((quad, i) => (
        <WindowSkyline key={`skyline-${scene.id}-${i}`} quad={quad} cw={size.w} ch={size.h} />
      ))}
      {ready && (shown.textureUrl || hexColor) && transformedShades.map((quad, i) => (
        <ProjectedShade
          key={`${scene.id}-${i}`}
          quad={quad}
          cw={size.w} ch={size.h}
          textureUrl={shown.textureUrl}
          hexColor={hexColor}
          opacity={opacity}
          mode={mode}
          ambience={scene.exterior ? 'exterior' : shownAmbience}
          hemFrac={hemFrac}
          railFrac={railFrac}
          system={system}
          fold={fold}
          body={!!light?.body}
          renderDrop={renderDrop}
          testId={`shade-zone-${i}`}
        />
      ))}
      {/* Foreground occlusion: furniture/objects in front of the glass stay visually in front of the fabric */}
      {scene.images?.foreground && ready && (
        <img src={scene.images.foreground} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover pointer-events-none" data-testid="room-foreground" />
      )}
      {children}
    </div>
  );
};

export default RoomMockup;
